import {
  complement,
  curry,
  filter,
  includes,
  isNil,
  map,
  path,
  pipe,
  pluck,
  prop,
  replace,
  sum
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { formatCurrency } from '../../../utils/currency'
import { hasInternetFinanceAddons } from '../../../utils/homeSolutions'
import { getGifts, isAOptikTVGift } from '../../organisms/Gift/utils'
import { getInternetService } from '../../organisms/Internet/utils'
import { getOptikTvService } from '../../organisms/OptikTV/utils'

const { hasContractTerm } = utils.cart

const isAInternetGift = pipe(prop('tags'), includes('internet'))

const constructLegalSlug = (cart) => {
  const gift = getGifts(cart)?.[0]
  const optikTvService = getOptikTvService(cart)
  const internetService = getInternetService(cart)

  const containsInternetFinanceAddons = hasInternetFinanceAddons(cart)
  const containsInTermOptikTVService = optikTvService ? hasContractTerm(optikTvService) : false
  const containsInTermInternetService = internetService ? hasContractTerm(internetService) : false

  if (containsInternetFinanceAddons && containsInTermOptikTVService && gift) {
    return {
      slug: 'nonIndexedLegalSlugs.internetFinanceAddonAndOptikTVAndGWPCancellationFeeLegalSlug',
      services: [optikTvService, gift]
    }
  }

  if (!containsInternetFinanceAddons && gift && isAOptikTVGift(gift)) {
    return {
      slug: 'nonIndexedLegalSlugs.internetAndOptikTVComboCancellationFeeLegalSlug',
      services: [internetService, optikTvService, gift]
    }
  }

  if (!containsInternetFinanceAddons && gift && isAInternetGift(gift)) {
    return {
      slug: 'nonIndexedLegalSlugs.internetAndGWPComboCancellationFeeLegalSlug',
      services: [internetService, gift]
    }
  }

  if (
    !containsInternetFinanceAddons &&
    !gift &&
    containsInTermInternetService &&
    containsInTermOptikTVService
  ) {
    return {
      slug: 'nonIndexedLegalSlugs.internetAndOptikTVComboCancellationFeeLegalSlug',
      services: [internetService, optikTvService]
    }
  }

  return null
}

const getCancellationFee = (service) => ({
  cancellationFeeAmount: path(['contractTerm', 'cancellationFee', 'amount'], service),
  cancellationFeeAmortizedAmount: path(
    ['contractTerm', 'cancellationFee', 'amortizedAmount'],
    service
  )
})

const getCancellationFees = pipe(filter(complement(isNil)), map(getCancellationFee))

const getTotalAmortizedCancellationFee = pipe(
  getCancellationFees,
  pluck('cancellationFeeAmortizedAmount'),
  sum
)

const getTotalCancellationFee = pipe(getCancellationFees, pluck('cancellationFeeAmount'), sum)

export const transformComboCancellationFeeContent = curry((services, lang, content) => {
  const totalCancellationFee = getTotalCancellationFee(services)
  const totalAmortizedCancellationFee = getTotalAmortizedCancellationFee(services)
  return pipe(
    replace('#totalCancellationFeeAmount#', formatCurrency(totalCancellationFee, lang)),
    replace(
      '#totalCancellationFeeAmortizedAmount#',
      formatCurrency(totalAmortizedCancellationFee, lang)
    )
  )(content)
})

export const getNonIndexedLegalKeys = ({ cart, isCartValid, translate }) => {
  if (!isCartValid) {
    return {
      legalKeys: translate('nonIndexedLegalSlugs.invalidCartCancellationFeeTandC'),
      hsServices: []
    }
  }

  const nonIndexedLegalSlug = constructLegalSlug(cart)

  const hsServices = nonIndexedLegalSlug?.services ?? []

  const nonIndexedComboLegalKey = nonIndexedLegalSlug?.slug
    ? `${translate(nonIndexedLegalSlug?.slug)}`
    : ''

  const isHSCart = cart?.root?.cartType === 'HS'

  const legalKeys = isHSCart
    ? `${nonIndexedComboLegalKey}${translate('nonIndexedLegalSlugs.cancellationFeeTaxesSlug')}`
    : `${nonIndexedComboLegalKey} `

  return {
    legalKeys,
    hsServices
  }
}
